import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Collapse,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Card,
  CardContent,
  Tooltip,
  Pagination,
  TextField,
  Stack,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  Flag as FlagIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import React, { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getRankings } from "../features/ranking/rankingSlice";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import FlagDialog from "../components/FlagDialog";
import FlagTutorial from "../components/FlagTutorial";
import AdminEntryActions from "../components/AdminEntryActions";
import { useSupabase } from "../providers/supabaseProvider";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useAccessControl } from "../context/AccessControlContext";

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB"); // DD/MM/YYYY format
};

const flagReasons = [
  "Incorrect Points",
  "Incorrect Position",
  "Incorrect District",
  "Incorrect Date",
  "Duplicate Entry",
  "Other",
];

function Home() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useKindeAuth();
  const { rankings, isLoading } = useSelector((state) => state.ranking);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [categoriesMap, setCategoriesMap] = useState(new Map());
  const [categoryTypesMap, setCategoryTypesMap] = useState(new Map());
  const [formatsMap, setFormatsMap] = useState(new Map());

  const { isAdmin } = useAccessControl();

  const [lookupsLoading, setLookupsLoading] = useState(true);
  const [flagDialog, setFlagDialog] = useState({
    open: false,
    event: null,
    playerName: null,
  });
  const [feedback, setFeedback] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [asOfDate, setAsOfDate] = useState("");
  const [bsaFilter, setBsaFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");

  // Use the Supabase provider instance
  const supabase = useSupabase();

  // Helper function to get category name from ID
  const getCategoryName = (categoryId) => {
    if (!categoryId) return "N/A";
    return categoriesMap.get(parseInt(categoryId)) || "N/A";
  };

  // Helper function to get category type name from ID
  const getCategoryTypeName = (categoryTypeId) => {
    if (!categoryTypeId && categoryTypeId !== 0) return "N/A";
    // If it's a string, it might be the name already (not an ID)
    if (typeof categoryTypeId === "string" && isNaN(categoryTypeId)) {
      return categoryTypeId;
    }
    // Try to look it up as an ID
    const typeId = parseInt(categoryTypeId);
    const name = categoryTypesMap.get(typeId);
    // Fallback to the raw ID if lookup fails
    return name || categoryTypeId || "N/A";
  };

  // Helper function to get format name from ID
  const getFormatName = (formatId) => {
    if (!formatId && formatId !== 0) return "N/A";
    const name = formatsMap.get(parseInt(formatId));
    return name || formatId || "N/A";
  };

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const [categoriesRes, categoryTypesRes, formatsRes] = await Promise.all([
          supabase.from("categories").select("id, name"),
          supabase.from("category_type").select("id, name"),
          supabase.from("formats").select("id, name"),
        ]);

        if (categoriesRes.error) throw categoriesRes.error;
        if (categoryTypesRes.error) {
          console.warn("Category types fetch failed:", categoryTypesRes.error);
        }
        if (formatsRes.error) {
          console.warn("Formats fetch failed:", formatsRes.error);
        }

        // Create lookup maps
        const catMap = new Map(
          categoriesRes.data.map((cat) => [cat.id, cat.name]),
        );
        const catTypeMap = new Map(
          (categoryTypesRes.data || []).map((ct) => [ct.id, ct.name]),
        );
        const formatMap = new Map(
          (formatsRes.data || []).map((f) => [f.id, f.name]),
        );

        setCategoriesMap(catMap);
        setCategoryTypesMap(catTypeMap);
        setFormatsMap(formatMap);
      } catch (error) {
        console.error("Error fetching lookup data:", error);
      } finally {
        setLookupsLoading(false);
      }
    };

    fetchCategories();
  }, [user?.id]);

  const toggleRow = (rowKey) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowKey)) {
      newExpanded.delete(rowKey);
    } else {
      newExpanded.add(rowKey);
    }
    setExpandedRows(newExpanded);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const filteredRankings = rankings.filter((rank) => {
    const playerName = (rank.player_name || "").toLowerCase();
    const bsaNumber = String(rank.bsa_number || "").toLowerCase();
    const bsaSearch = bsaFilter.trim().toLowerCase();
    const nameTerms = nameFilter
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);

    const matchesBsa = !bsaSearch || bsaNumber.includes(bsaSearch);
    const matchesName =
      nameTerms.length === 0 ||
      nameTerms.every((term) => playerName.includes(term));

    return matchesBsa && matchesName;
  });

  // Calculate paginated rankings
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedRankings = filteredRankings.slice(startIndex, endIndex);
  const totalPages = Math.max(
    1,
    Math.ceil(filteredRankings.length / rowsPerPage),
  );

  const handleOpenFlagDialog = (event) => {
    setFlagDialog({ open: true, event });
  };

  const handleCloseFlagDialog = () => {
    setFlagDialog({ open: false, event: null, playerName: null });
  };

  const handleCloseFeedback = () => {
    setFeedback({ ...feedback, open: false });
  };

  const fetchRankings = (dateValue = "") => {
    const params = isAdmin && dateValue ? { date: dateValue } : undefined;
    dispatch(getRankings(params));
  };

  const handleApplyAsOfDate = () => {
    setCurrentPage(1);
    fetchRankings(asOfDate);
  };

  const handleClearAsOfDate = () => {
    setAsOfDate("");
    setCurrentPage(1);
    fetchRankings("");
  };

  useEffect(() => {
    dispatch(getRankings());
  }, [dispatch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [bsaFilter, nameFilter]);

  return (
    <Box
      sx={{ padding: { xs: 1, sm: 2 } }}
      display="flex"
      flexDirection="column"
      gap={2}
    >
      <FlagTutorial />
      <Box display="flex" justifyContent="flex-start">
        {/* {user && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/entries")}
          >
            Entries
          </Button>
        )} */}
      </Box>

      {/* Top 3 Players Chart */}
      {/* {rankings.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: "700",
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  fontFamily: "'Roboto', sans-serif",
                  letterSpacing: "0.5px",
                }}
              >
                🏆 Top 3 Players Progression
              </Typography>
            </Box>
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: 2,
                padding: { xs: 1, sm: 2 },
              }}
            >
              <ResponsiveContainer width="100%" height={{ xs: 250, sm: 350 }}>
                <LineChart
                  data={(() => {
                    // Get top 3 players
                    const top3 = rankings.slice(0, 3);

                    // Collect all unique dates from all top 3 players' events
                    const allDates = new Set();
                    top3.forEach((player) => {
                      if (player.qualifying_events) {
                        player.qualifying_events.forEach((event) => {
                          allDates.add(new Date(event.date).getTime());
                        });
                      }
                    });

                    // Sort dates chronologically
                    const sortedDates = Array.from(allDates).sort(
                      (a, b) => a - b
                    );

                    // Create chart data
                    return sortedDates.map((dateTime) => {
                      const dateObj = new Date(dateTime);
                      const dateStr = dateObj.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      });

                      const dataPoint = { date: dateStr };

                      // Calculate cumulative points for each top 3 player at this date
                      top3.forEach((player, idx) => {
                        const cumulativePoints = player.qualifying_events
                          .filter((e) => new Date(e.date) <= dateObj)
                          .reduce((sum, e) => sum + e.points, 0);

                        dataPoint[player.player_name] = cumulativePoints;
                      });

                      return dataPoint;
                    });
                  })()}
                  margin={{ top: 10, right: { xs: 10, sm: 30 }, left: 0, bottom: 10 }}
                >
                  <defs>
                    <linearGradient
                      id="colorPlayer1"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#e91e63" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#e91e63" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient
                      id="colorPlayer2"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#9c27b0" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#9c27b0" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient
                      id="colorPlayer3"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#ff9800" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#ff9800" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    stroke="#e0e0e0"
                    strokeDasharray=""
                    horizontal={true}
                    vertical={true}
                  />
                  <XAxis
                    dataKey="date"
                    stroke="#666"
                    style={{
                      fontSize: "12px",
                      fontFamily: "'Roboto', sans-serif",
                    }}
                  />
                  <YAxis
                    stroke="#666"
                    style={{
                      fontSize: "12px",
                      fontFamily: "'Roboto', sans-serif",
                    }}
                  />
                  <RechartsTooltip
                    formatter={(value) => `${value.toFixed(2)} pts`}
                    contentStyle={{
                      backgroundColor: "rgba(255, 255, 255, 0.98)",
                      border: "2px solid #667eea",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      padding: "10px",
                      fontFamily: "'Roboto', sans-serif",
                    }}
                    cursor={{ stroke: "#667eea" }}
                  />
                  <Legend
                    wrapperStyle={{ fontFamily: "'Roboto', sans-serif" }}
                  />
                  {rankings.slice(0, 3).map((player, idx) => (
                    <Line
                      key={idx}
                      type="monotone"
                      dataKey={player.player_name}
                      stroke={
                        idx === 0
                          ? "#e91e63"
                          : idx === 1
                          ? "#9c27b0"
                          : "#ff9800"
                      }
                      strokeWidth={3}
                      dot={{ r: 5, strokeWidth: 2, stroke: "white" }}
                      activeDot={{ r: 7, strokeWidth: 2 }}
                      fillOpacity={1}
                      fill={
                        idx === 0
                          ? "url(#colorPlayer1)"
                          : idx === 1
                          ? "url(#colorPlayer2)"
                          : "url(#colorPlayer3)"
                      }
                      isAnimationActive={true}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )} */}

      <Box sx={{ mt: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ mb: 2 }}
        >
          <TextField
            label="Filter by BSA number"
            size="small"
            value={bsaFilter}
            onChange={(event) => setBsaFilter(event.target.value)}
          />
          <TextField
            label="Filter by player name"
            size="small"
            value={nameFilter}
            onChange={(event) => setNameFilter(event.target.value)}
            helperText="Matches first name and last name"
          />
        </Stack>

        {isAdmin && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ mb: 2 }}
          >
            <TextField
              label="As of date"
              type="date"
              size="small"
              value={asOfDate}
              onChange={(event) => setAsOfDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button variant="contained" onClick={handleApplyAsOfDate}>
              Apply
            </Button>
            <Button variant="outlined" onClick={handleClearAsOfDate}>
              Clear
            </Button>
          </Stack>
        )}

        {isLoading ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            sx={{ py: 8 }}
          >
            <CircularProgress size={60} />
            <Typography variant="body1" sx={{ mt: 2 }} color="text.secondary">
              Loading rankings...
            </Typography>
          </Box>
        ) : filteredRankings.length === 0 ? (
          <Typography variant="body1" color="text.secondary">
            No rankings match your filters.
          </Typography>
        ) : (
          <Fragment>
            <Box sx={{ overflowX: "auto", width: "100%" }}>
              <Table
                sx={{
                  minWidth: { xs: 400, sm: 650 },
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ py: 1, fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                    />
                    <TableCell
                      sx={{
                        py: 1,
                        px: 1,
                        fontSize: { xs: "0.7rem", sm: "0.875rem" },
                      }}
                    >
                      Rank
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1,
                        px: 1,
                        fontSize: { xs: "0.7rem", sm: "0.875rem" },
                      }}
                    >
                      Player
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1,
                        px: 1,
                        fontSize: { xs: "0.7rem", sm: "0.875rem" },
                      }}
                    >
                      Points
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRankings.map((rank) => {
                    const rowKey = rank.rank + rank.player_name;
                    const isExpanded = expandedRows.has(rowKey);
                    return (
                      <React.Fragment key={rowKey}>
                        <TableRow sx={{ height: "auto" }}>
                          <TableCell
                            sx={{
                              py: 0.5,
                              px: 0.5,
                              fontSize: { xs: "0.7rem", sm: "0.875rem" },
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => toggleRow(rowKey)}
                              sx={{ p: 0.5 }}
                            >
                              {isExpanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </TableCell>
                          <TableCell
                            sx={{
                              py: 0.5,
                              px: 1,
                              fontSize: { xs: "0.7rem", sm: "0.875rem" },
                            }}
                          >
                            {rank.rank}
                          </TableCell>
                          <TableCell
                            sx={{
                              py: 0.5,
                              px: 1,
                              fontSize: { xs: "0.7rem", sm: "0.875rem" },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              {rank.player_name}
                              {isAdmin && rank.user_profile && (
                                <Tooltip title="Has user profile">
                                  <CheckCircleIcon
                                    sx={{
                                      fontSize: "1rem",
                                      color: "success.main",
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{
                              py: 0.5,
                              px: 1,
                              fontSize: { xs: "0.7rem", sm: "0.875rem" },
                            }}
                          >
                            {rank.total_points}
                          </TableCell>
                        </TableRow>
                        <TableRow key={`${rowKey}-details`}>
                          <TableCell
                            style={{ paddingBottom: 0, paddingTop: 0 }}
                            colSpan={4}
                          >
                            <Collapse
                              in={isExpanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={{ margin: 1 }}>
                                <Typography
                                  variant="h6"
                                  gutterBottom
                                  component="div"
                                >
                                  Qualifying Events
                                </Typography>
                                {/* Group events by year and format date */}
                                {(() => {
                                  const eventsByYear = {};
                                  rank.qualifying_events?.forEach((event) => {
                                    const dateObj = event.date
                                      ? new Date(event.date)
                                      : null;
                                    const year = dateObj
                                      ? dateObj.getFullYear()
                                      : "Unknown";
                                    if (!eventsByYear[year])
                                      eventsByYear[year] = [];
                                    eventsByYear[year].push({
                                      ...event,
                                      formattedDate: dateObj
                                        ? `${String(dateObj.getDate()).padStart(
                                            2,
                                            "0",
                                          )}-${String(
                                            dateObj.getMonth() + 1,
                                          ).padStart(
                                            2,
                                            "0",
                                          )}-${dateObj.getFullYear()}`
                                        : event.date,
                                    });
                                  });
                                  return Object.entries(eventsByYear)
                                    .sort((a, b) => Number(b[0]) - Number(a[0]))
                                    .map(([year, events]) => (
                                      <Box key={year} sx={{ mb: 2 }}>
                                        <Typography
                                          variant="subtitle1"
                                          sx={{ fontWeight: "bold", mt: 2 }}
                                        >
                                          {year}
                                        </Typography>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell sx={{ width: 120 }}>
                                                Date
                                              </TableCell>
                                              <TableCell>Event Name</TableCell>
                                              <TableCell sx={{ width: 130 }}>
                                                Category
                                              </TableCell>
                                              <TableCell sx={{ width: 130 }}>
                                                Category Type
                                              </TableCell>
                                              <TableCell sx={{ width: 130 }}>
                                                Format
                                              </TableCell>
                                              <TableCell sx={{ width: 130 }}>
                                                District
                                              </TableCell>
                                              <TableCell sx={{ width: 140 }}>
                                                Position
                                              </TableCell>
                                              <TableCell sx={{ width: 100 }}>
                                                Points
                                              </TableCell>
                                              <TableCell sx={{ width: 100 }}>
                                                Actions
                                              </TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {events.map((event, index) => (
                                              <TableRow key={index}>
                                                <TableCell sx={{ width: 120 }}>
                                                  {event.formattedDate}
                                                </TableCell>
                                                <TableCell>
                                                  {event.name}
                                                </TableCell>
                                                <TableCell sx={{ width: 130 }}>
                                                  {getCategoryName(
                                                    event.category,
                                                  )}
                                                </TableCell>
                                                <TableCell sx={{ width: 130 }}>
                                                  {getCategoryTypeName(
                                                    event.category_type,
                                                  )}
                                                </TableCell>
                                                <TableCell sx={{ width: 130 }}>
                                                  {getFormatName(event.format)}
                                                </TableCell>
                                                <TableCell sx={{ width: 130 }}>
                                                  {event.district}
                                                </TableCell>
                                                <TableCell sx={{ width: 140 }}>
                                                  {event.position}
                                                </TableCell>
                                                <TableCell sx={{ width: 100 }}>
                                                  {event.points}
                                                </TableCell>
                                                <TableCell sx={{ width: 100 }}>
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      gap: 1,
                                                      alignItems: "center",
                                                    }}
                                                  >
                                                    <Chip
                                                      data-tutorial="flag-button"
                                                      icon={<FlagIcon />}
                                                      label="Flag"
                                                      variant="outlined"
                                                      color="error"
                                                      size="small"
                                                      onClick={() => {
                                                        if (!user) {
                                                          setFeedback({
                                                            open: true,
                                                            message:
                                                              "You must be logged in to flag entries.",
                                                            severity: "warning",
                                                          });
                                                          return;
                                                        }
                                                        setFlagDialog({
                                                          open: true,
                                                          event: {
                                                            name: event.name,
                                                            date: event.date,
                                                            formattedDate:
                                                              formatDate(
                                                                event.date,
                                                              ),
                                                            position:
                                                              event.position ||
                                                              "N/A",
                                                            entryId: event.id,
                                                          },
                                                          playerName:
                                                            rank.player_name,
                                                        });
                                                      }}
                                                      disabled={!user}
                                                    />
                                                    <AdminEntryActions
                                                      entry={{
                                                        ...event,
                                                        id: event.id,
                                                      }}
                                                      onActionComplete={(
                                                        action,
                                                      ) => {
                                                        setFeedback({
                                                          open: true,
                                                          message: `Entry ${action.replace(
                                                            /_/g,
                                                            " ",
                                                          )} successfully.`,
                                                          severity: "success",
                                                        });
                                                        // Optionally reload rankings
                                                        fetchRankings(asOfDate);
                                                      }}
                                                    />
                                                  </Box>
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </Box>
                                    ));
                                })()}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
              {/* Pagination Controls */}
              <Box
                sx={{ display: "flex", justifyContent: "center", mt: 3, mb: 2 }}
              >
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      fontSize: { xs: "0.75rem", sm: "1rem" },
                      minWidth: { xs: "28px", sm: "40px" },
                      height: { xs: "28px", sm: "40px" },
                    },
                  }}
                />
              </Box>
            </Box>
          </Fragment>
        )}
      </Box>

      {/* Flag Entry Dialog */}
      <FlagDialog
        open={flagDialog.open}
        onClose={handleCloseFlagDialog}
        event={flagDialog.event}
        playerName={flagDialog.playerName}
      />

      {/* Feedback Snackbar */}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={handleCloseFeedback}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseFeedback}
          severity={feedback.severity}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Home;
